// src/components/profiles/GuestUser/components/moments/GuestMomentMyGrid.tsx

"use client";

import React, { useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouter } from "next/navigation";

import type { AppDispatch } from "../../../../../redux/storeTypes";
import { fetchMyMoments } from "../../../../../redux/ugc/momentSlice";
import {
  selectMyMoments,
  selectMyMomentsLoading,
  selectMyMomentsHasMore,
} from "../../../../../redux/selectors/ugc/momentSelectors";

import { Loader } from "../../../../common";
import GuestMomentGridItem from "./GuestMomentGridItem";
import GuestMomentEmptyState from "./GuestMomentEmptyState";

import "./GuestMomentMyGrid.css";

const GuestMomentMyGrid: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const router = useRouter();

  const moments = useSelector(selectMyMoments);
  const loading = useSelector(selectMyMomentsLoading);
  const hasMore = useSelector(selectMyMomentsHasMore);

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Prevent duplicate first-page fetch in StrictMode/remount bursts.
  const didInitialFetchRef = useRef(false);

  // Keep latest values for IntersectionObserver without rebuilding too often.
  const loadingRef = useRef(loading);
  const hasMoreRef = useRef(hasMore);

  // Prevent rapid duplicate pagination dispatches.
  const nextPageLockedRef = useRef(false);

  useEffect(() => {
    loadingRef.current = loading;

    if (!loading) {
      nextPageLockedRef.current = false;
    }
  }, [loading]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  // Initial fetch.
  useEffect(() => {
    if (didInitialFetchRef.current) return;

    didInitialFetchRef.current = true;
    nextPageLockedRef.current = false;

    dispatch(fetchMyMoments({ page: 1 }));
  }, [dispatch]);

  const handleCreate = useCallback(() => {
    router.push("/moments/create");
  }, [router]);

  // Load next page safely.
  const loadNext = useCallback(() => {
    if (nextPageLockedRef.current) return;
    if (loadingRef.current) return;
    if (!hasMoreRef.current) return;

    nextPageLockedRef.current = true;

    dispatch(fetchMyMoments());
  }, [dispatch]);

  // Infinite scroll observer.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;

        loadNext();
      },
      {
        root: null,
        rootMargin: "220px 0px",
        threshold: 0,
      }
    );

    io.observe(el);

    return () => {
      io.disconnect();
    };
  }, [loadNext]);

  if (loading && moments.length === 0) {
    return (
      <div className="guest-moment-my-grid__loading">
        <Loader variant="dots" size="md" color="glory" />
      </div>
    );
  }

  if (!loading && moments.length === 0) {
    return <GuestMomentEmptyState onCreate={handleCreate} />;
  }

  return (
    <section className="guest-moment-my-grid" aria-label="My Moments">
      <div className="guest-moment-my-grid__list">
        {moments.map((moment) => (
          <GuestMomentGridItem
            key={moment.id ?? moment.slug}
            moment={moment}
          />
        ))}
      </div>

      <div ref={sentinelRef} className="guest-moment-my-grid__sentinel" />

      {loading && moments.length > 0 && (
        <div className="guest-moment-my-grid__more">
          <Loader variant="dots" size="md" color="glory" />
        </div>
      )}

      {!hasMore && moments.length > 0 && (
        <div className="guest-moment-my-grid__endHint">
          You’re all caught up on Moments ✨
        </div>
      )}
    </section>
  );
};

export default GuestMomentMyGrid;